import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import { checkServerConnection } from '../lib/api';
import api from '../lib/api';
import { AlertCircle, Fingerprint, ChevronRight, WifiOff, RefreshCw, UserPlus, ArrowLeft, Lock, Mail, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function Login() {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [error, setError] = useState('');
  const [serverOnline, setServerOnline] = useState(true);
  const [checkingServer, setCheckingServer] = useState(true);
  const { login, loginWithBiometric } = useAuth();
  const { isAvailable, authenticateWithBiometric } = useBiometricAuth();
  const navigate = useNavigate();

  // Register fields
  const [regNom, setRegNom] = useState('');
  const [regPrenom, setRegPrenom] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  
  const hasBiometricCredential = localStorage.getItem('biometricCredentialId');

  useEffect(() => {
    const checkServer = async () => {
      setCheckingServer(true);
      try {
        const isOnline = await checkServerConnection();
        setServerOnline(isOnline);
      } catch (error) {
        console.log('Health check error, assuming online:', error);
        setServerOnline(true);
      }
      setCheckingServer(false);
    };
    
    const timeoutId = setTimeout(() => {
      if (checkingServer) {
        setCheckingServer(false);
        setServerOnline(true);
      }
    }, 3000);
    
    checkServer();
    return () => clearTimeout(timeoutId);
  }, []);

  const handleRetryConnection = async () => {
    setCheckingServer(true);
    setError('');
    const isOnline = await checkServerConnection();
    setServerOnline(isOnline);
    setCheckingServer(false);
    if (isOnline) {
      toast.success('Connexion au serveur rétablie');
    } else {
      toast.error('Le serveur est toujours indisponible');
    }
  };

  const handleBiometricLogin = async () => {
    setError('');
    setBiometricLoading(true);
    try {
      const result = await authenticateWithBiometric();
      if (result) {
        await loginWithBiometric(result);
        toast.success('Connexion biométrique réussie');
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Échec de l\'authentification biométrique');
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      if (!navigator.onLine) {
        toast.success('Connexion hors ligne réussie');
      } else {
        toast.success('Connexion réussie');
      }
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      if (err.message?.includes('hors ligne') || err.message?.includes('offline')) {
        setError(err.message);
      } else if (err.message?.includes('incorrect')) {
        setError(err.message);
      } else {
        const errorDetail = err.response?.data?.detail;
        if (typeof errorDetail === 'string') {
          setError(errorDetail);
        } else if (err.response?.status === 401) {
          setError('Email ou mot de passe incorrect');
        } else if (err.response?.status === 403) {
          setError('Compte désactivé. Contactez l\'administrateur.');
        } else if (!err.response && !navigator.onLine) {
          setError('Connectez-vous en ligne une première fois pour activer le mode hors ligne.');
        } else if (!err.response) {
          setError('Impossible de joindre le serveur.');
          setServerOnline(false);
        } else {
          setError('Erreur de connexion. Réessayez.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (regPassword !== regPasswordConfirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (regPassword.length < 4) {
      setError('Le mot de passe doit contenir au moins 4 caractères');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/register', {
        email: regEmail,
        password: regPassword,
        nom: regNom,
        prenom: regPrenom
      });
      setRegisterSuccess(true);
      toast.success('Compte créé avec succès');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        setError(errorDetail);
        toast.error(errorDetail);
      } else {
        setError('Erreur lors de la création du compte');
        toast.error('Erreur lors de la création');
      }
    } finally {
      setLoading(false);
    }
  };

  const switchToLogin = () => {
    setMode('login');
    setError('');
    setRegisterSuccess(false);
  };

  const switchToRegister = () => {
    setMode('register');
    setError('');
    setRegisterSuccess(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute -top-1/2 -left-1/2 w-full h-full rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(229, 57, 53, 0.3) 0%, transparent 60%)' }}
        />
        <div 
          className="absolute -bottom-1/4 -right-1/4 w-3/4 h-3/4 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(0, 229, 255, 0.2) 0%, transparent 60%)' }}
        />
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-6 safe-area-top safe-area-bottom">
        {/* Logo */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-wider mb-2" style={{ color: '#FFFFFF' }}>
            BYTRANSPORT
          </h1>
          <p className="text-xs sm:text-sm tracking-[0.2em] uppercase font-light" style={{ color: '#FFFFFF' }}>
            GESTION DE FLOTTE & TRANSPORT
          </p>
        </div>

        <div className="w-full max-w-md animate-slide-up">
          <div className="glass-panel p-8">
            
            {/* ===== LOGIN MODE ===== */}
            {mode === 'login' && (
              <>
                <h2 className="text-2xl font-bold text-white uppercase tracking-wide mb-6">
                  Connexion
                </h2>
                
                {!serverOnline && !checkingServer && (
                  <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="server-offline-warning">
                    <div className="flex items-start gap-3">
                      <WifiOff size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-amber-200 text-sm font-medium">Mode hors ligne possible</p>
                        <p className="text-amber-200/70 text-xs mt-1">Si vous vous êtes déjà connecté, vous pouvez accéder à vos données en cache.</p>
                      </div>
                      <button type="button" onClick={handleRetryConnection} className="p-2 hover:bg-amber-500/20 rounded-lg transition-colors" title="Réessayer">
                        <RefreshCw size={16} className="text-amber-400" />
                      </button>
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="alert-item mb-6 flex items-start gap-3" data-testid="login-error">
                    <AlertCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-red-300">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="email" className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Email</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre.email@exemple.com" required data-testid="email-input" className="input-dark w-full h-12" />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Mot de passe</Label>
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••" required data-testid="password-input" className="input-dark w-full h-12" />
                  </div>
                  <Button type="submit" variant="brand" className="w-full h-12 text-base" disabled={loading} data-testid="login-button">
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Connexion...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Se connecter <ChevronRight size={20} />
                      </span>
                    )}
                  </Button>
                </form>

                {isAvailable && hasBiometricCredential && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <Button type="button" variant="outline" className="btn-glass w-full h-12 flex items-center justify-center gap-3"
                      onClick={handleBiometricLogin} disabled={biometricLoading} data-testid="biometric-login-button">
                      <Fingerprint size={22} className="text-cyan-400" />
                      <span className="text-white">{biometricLoading ? 'Vérification...' : 'Touch ID / Face ID'}</span>
                    </Button>
                  </div>
                )}

                {/* Link to register */}
                <div className="mt-6 pt-6 border-t border-white/10 text-center">
                  <p className="text-zinc-500 text-sm mb-3">Pas encore de compte ?</p>
                  <button
                    type="button"
                    onClick={switchToRegister}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-colors text-sm font-medium"
                    data-testid="switch-to-register"
                  >
                    <UserPlus size={18} className="text-cyan-400" />
                    Créer un compte
                  </button>
                </div>
              </>
            )}

            {/* ===== REGISTER MODE ===== */}
            {mode === 'register' && !registerSuccess && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <button type="button" onClick={switchToLogin}
                    className="p-2 rounded-lg hover:bg-white/10 text-zinc-400 transition-colors" data-testid="back-to-login">
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="text-2xl font-bold text-white uppercase tracking-wide">
                    Créer un compte
                  </h2>
                </div>

                {error && (
                  <div className="alert-item mb-6 flex items-start gap-3" data-testid="register-error">
                    <AlertCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-red-300">{error}</span>
                  </div>
                )}

                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="reg-prenom" className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Prénom</Label>
                      <Input id="reg-prenom" type="text" value={regPrenom} onChange={(e) => setRegPrenom(e.target.value)}
                        placeholder="Jean" required data-testid="reg-prenom" className="input-dark w-full h-11" />
                    </div>
                    <div>
                      <Label htmlFor="reg-nom" className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Nom</Label>
                      <Input id="reg-nom" type="text" value={regNom} onChange={(e) => setRegNom(e.target.value)}
                        placeholder="Dupont" required data-testid="reg-nom" className="input-dark w-full h-11" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="reg-email" className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Email</Label>
                    <Input id="reg-email" type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)}
                      placeholder="votre.email@exemple.com" required data-testid="reg-email" className="input-dark w-full h-11" />
                  </div>
                  <div>
                    <Label htmlFor="reg-password" className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Mot de passe</Label>
                    <Input id="reg-password" type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)}
                      placeholder="••••••••" required data-testid="reg-password" className="input-dark w-full h-11" />
                  </div>
                  <div>
                    <Label htmlFor="reg-password-confirm" className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Confirmer le mot de passe</Label>
                    <Input id="reg-password-confirm" type="password" value={regPasswordConfirm} onChange={(e) => setRegPasswordConfirm(e.target.value)}
                      placeholder="••••••••" required data-testid="reg-password-confirm" className="input-dark w-full h-11" />
                    {regPasswordConfirm && regPassword !== regPasswordConfirm && (
                      <p className="text-red-400 text-xs mt-1">Les mots de passe ne correspondent pas</p>
                    )}
                  </div>

                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-amber-300 text-xs">
                      Votre compte sera créé sans accès. Un administrateur devra activer vos permissions.
                    </p>
                  </div>

                  <Button type="submit" variant="brand" className="w-full h-12 text-base" disabled={loading} data-testid="register-button">
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Création...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <UserPlus size={18} />
                        Créer mon compte
                      </span>
                    )}
                  </Button>
                </form>
              </>
            )}

            {/* ===== REGISTER SUCCESS ===== */}
            {mode === 'register' && registerSuccess && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <UserPlus size={32} className="text-green-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Compte créé !</h2>
                <p className="text-zinc-400 text-sm mb-6">
                  Votre compte a été créé avec succès. Un administrateur doit maintenant activer vos accès avant que vous puissiez vous connecter.
                </p>
                <Button onClick={switchToLogin} variant="brand" className="w-full h-12" data-testid="back-to-login-after-register">
                  <span className="flex items-center justify-center gap-2">
                    <ArrowLeft size={18} />
                    Retour à la connexion
                  </span>
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-zinc-600 tracking-wider">
              POWERED BY YASSINE BELHADJ
            </p>
          </div>
        </div>
      </div>

      {/* PWA Install Hint */}
      <div className="md:hidden relative z-10 p-4 text-center">
        <p className="text-xs text-zinc-600">
          Ajoutez à l'écran d'accueil pour une meilleure expérience
        </p>
      </div>
    </div>
  );
}
